import {Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography, useTheme} from "@mui/material";
import React, {ChangeEvent, ForwardedRef, forwardRef, useCallback, useEffect, useState} from "react";
import {PathValue, useController} from "react-hook-form";
import {FieldPath, FieldValues} from "react-hook-form/dist/types";
import {OptionValue} from "../../common/types/optionValue.type";
import {BaseMultiChoiceInputProps} from "../../common/types/baseMultiChoiceInputProps.type";
import {Option} from "../../common/types/option.type";

type RadioGroupInputProps<T extends FieldValues, TName extends FieldPath<T>, TItem> = BaseMultiChoiceInputProps<T, TName, TItem> & {
    inline?: boolean;
    noRadioLabel?: boolean;
    fieldMapper: (value: OptionValue, items: TItem[]) => PathValue<T, TName> | undefined;
    valueMapper: (value: PathValue<T, TName>, items: TItem[]) => OptionValue;
}

const RadioGroupInput = forwardRef(<T extends FieldValues, TName extends FieldPath<T>, TItem>(
    props: RadioGroupInputProps<T, TName, TItem>,
    ref: ForwardedRef<HTMLInputElement>
) => {

    const theme = useTheme();

    const {
        label,
        hint,
        disabled,
        errorMessage,
        error,
        inline,
        noRadioLabel,
        items,
        getOptionLabel,
        getOptionValue,
        fieldMapper,
        valueMapper,
        ...rest
    } = props;

    const {field} = useController({
        ...rest
    });

    const {onChange, value, ref: inputRef, ...otherField} = field;

    const [innerValue, setInnerValue] = useState<OptionValue>("");
    const [options, setOptions] = useState<Option[]>([]);

    useEffect(() => {
        if (!value) {
            setInnerValue("");
        } else {
            setInnerValue(valueMapper(value, items));
        }
        setOptions(items.map(i => ({label: getOptionLabel(i), value: getOptionValue(i)})));

    }, [value, items, getOptionLabel, getOptionValue]);

    const onChanged = useCallback((event: ChangeEvent<HTMLInputElement>, value: OptionValue) => {
        onChange(fieldMapper(value, items))
    }, [fieldMapper, items, onChange]);

    const combineRefs = useCallback((node: HTMLInputElement | null) => {
        // Handle the ref from useController (inputRef)
        if (typeof inputRef === 'function') {
            inputRef(node);
        } else if (inputRef) {
            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }

        // Handle the forwarded ref from the parent component
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }
    }, [inputRef, ref]);

    return (
        <Box>
            <FormControl error={error} fullWidth disabled={disabled}>
                {label && (
                    <FormLabel id={`${field.name}-label`} className={"input-label"} sx={{mb: 0.5}}>
                        {label}
                    </FormLabel>
                )}
                <RadioGroup
                    ref={combineRefs}
                    row={inline}
                    id={field.name}
                    aria-labelledby={`${field.name}-label`}
                    {...rest}
                    {...otherField}
                    onChange={onChanged}
                    value={innerValue}
                    sx={{
                        ...(inline && {
                            flexDirection: "row",
                            flex: 1,
                            justifyContent: noRadioLabel ? "space-between" : "space-around",
                        })
                    }}
                >
                     {/*Hidden field to initialize the value (mount/unmount issue occurs without this e.g. scaleQuiz) */}
                    <input
                        type="hidden"
                        value={innerValue ?? ""}
                        name={field.name}
                        ref={inputRef}
                    />

                    {options.map((option, index) => (
                        <FormControlLabel
                            key={index}
                            value={option.value}
                            className={"input-label"}
                            control={<Radio />}
                            label={option.label}
                            componentsProps={{typography: {variant: "body2"}}}
                            sx={{
                                marginTop: inline ? 0 : 1,
                                ...(noRadioLabel && {
                                    marginX: 0,
                                })
                            }}
                        />
                    ))}
                </RadioGroup>
                {hint &&
                    <Typography component="div" variant={"body2"} className={"input-help"}>
                        {hint}
                    </Typography>
                }
                {errorMessage &&
                    <Typography component="div" variant={"body2"} className={"error-message"}>
                        {errorMessage}
                    </Typography>
                }
            </FormControl>
        </Box>
    )
})

export {RadioGroupInput}